import {
  Avatar,
  Button,
  Card,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { IDistrictInfo } from 'API/contacts';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import { useDictionary, useIsDesktop } from 'Hooks';
import { PurpleFax, PurpleLocation, PurplePhone } from 'Images';
import { LinkArrowLeft2 } from 'Components';
import { districtType } from '../GeneralInfo';

interface ILocationInfo {
  district: IDistrictInfo;
  haveContacts: boolean;
}

export const LocationInfo = ({ district, haveContacts }: ILocationInfo) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.PersonalTeam);
  const isDesktop = useIsDesktop();

  return (
    <Stack gap={isDesktop ? '16px' : '12px'}>
      <Typography variant={'body3'}>
        {dictionary.districtBox.district}{' '}
        <strong>
          {dictionary.district} {district?.name}
        </strong>
      </Typography>
      {district?.address && (
        <Card
          elevation={0}
          sx={{
            border: '0.8px solid',
            borderColor: 'primary.shade_400',
            borderRadius: '16px',
            padding: '24px 32px 40px',
          }}
        >
          {haveContacts ? (
            <Stack>
              <Stack gap={isDesktop ? '12px' : '4px'}>
                <Typography variant={'body1'} component='h2'>
                  {dictionary.districtBox.address}
                </Typography>
                <Stack
                  direction={isDesktop ? 'row' : 'column'}
                  justifyContent={'space-between'}
                  gap={isDesktop ? '0px' : '16px'}
                >
                  <Typography variant={'body3'}>{district.address}</Typography>
                  {district.arrivalInstructions && (
                    <Stack
                      direction='row'
                      gap={'6px'}
                      justifyContent={'flex-end'}
                      sx={{
                        width: 'fit-content',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Avatar
                        alt={dictionary.districtBox.navigation}
                        src={PurpleLocation}
                        sx={{ width: '24px', height: '24px' }}
                      />
                      <Link
                        underline={'hover'}
                        role='link'
                        href={district.arrivalInstructions}
                        target={'_blank'}
                        rel={'noopener'}
                        onKeyDown={onEnterOrEscapeKeyDown}
                      >
                        <Typography variant={'body3'}>
                          {dictionary.districtBox.navigation}
                        </Typography>
                      </Link>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Stack>
          ) : (
            <>
              <Stack
                direction={isDesktop ? 'row' : 'column'}
                spacing={2}
                justifyContent={'space-between'}
              >
                <Stack spacing={2} mb={isDesktop ? undefined : 2}>
                  <Typography variant={'body1'}>
                    {dictionary.districtBox.address}
                  </Typography>
                  <Typography variant={'body3'}>{district.address}</Typography>
                  {district.arrivalInstructions && (
                    <Stack
                      direction='row'
                      gap={'6px'}
                      justifyContent={'flex-start'}
                    >
                      <Avatar
                        alt={dictionary.districtBox.navigation}
                        src={PurpleLocation}
                        sx={{ width: '24px', height: '24px' }}
                      />
                      <Link
                        underline={'hover'}
                        role='navigation'
                        href={district.arrivalInstructions}
                        target={'_blank'}
                        rel={'noopener'}
                        onKeyDown={onEnterOrEscapeKeyDown}
                      >
                        <Typography variant={'body3'}>
                          {dictionary.districtBox.navigation}
                        </Typography>
                      </Link>
                    </Stack>
                  )}
                </Stack>

                {district.phone && (
                  <Stack spacing={2}>
                    <Typography variant={'body1'}>
                      {dictionary.districtBox.phoneContact}
                    </Typography>

                    <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
                      <Avatar
                        alt=''
                        src={PurplePhone}
                        sx={{ width: '24px', height: '24px' }}
                      />
                      <Typography component='span' variant='body6'>
                        {district.phone}
                      </Typography>
                    </Stack>

                    <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
                      <Avatar
                        alt=''
                        src={PurpleFax}
                        sx={{ width: '24px', height: '24px' }}
                      />
                      <Typography component='span' variant='body6'>
                        {district.fax}
                      </Typography>
                    </Stack>
                  </Stack>
                )}

                <Stack spacing={2} maxWidth={'255px'}>
                  <Typography variant={'body3'}>
                    {dictionary.districtBox.appointmentNotice}
                  </Typography>

                  <Button
                    variant='contained'
                    sx={{
                      fontSize: isDesktop ? '18px' : '16px',
                      width: isDesktop ? '235px' : '211px',
                    }}
                    onClick={() => {
                      window.open(
                        'https://myvisit.com/#!/home/signin/',
                        '_blank'
                      );
                    }}
                  >
                    {dictionary.districtBox.createTicket}
                  </Button>
                </Stack>
              </Stack>
              <Divider
                aria-hidden='true'
                sx={{ borderColor: 'primary.shade_200', m: '32px 0' }}
              />
              <Stack flexDirection={'row'} justifyContent={'flex-end'}>
                <LinkArrowLeft2
                  href={
                    district.districtId === districtType.DAN
                      ? 'https://shikum.mod.gov.il/ContactUs/dan'
                      : `https://shikum.mod.gov.il/ContactUs/Pages/ContactUsCity.aspx?itemId=${district.districtId}`
                  }
                  text={dictionary.districtBox.moreDetails}
                  newWindow={true}
                />
              </Stack>
            </>
          )}
        </Card>
      )}
    </Stack>
  );
};
